import { MarketCategory } from '@vertex-protocol/react-client';

type InstitutionalMetadata = {
  symbol?: string;
  token?: {
    symbol?: string;
  };
  marketName?: string;
  marketCategories?: Set<MarketCategory>;
};

export const ARCA_INSTITUTIONAL_MARKET_CATEGORIES = [
  'commodity',
  'spot',
  'equities',
  'forex',
  'index',
] as const satisfies readonly MarketCategory[];

export const ARCA_INSTITUTIONAL_MARKET_BY_PRODUCT_ID = {
  28: ['XAUT', 'XAUTUSDT0'],
  88: ['XAG', 'XAG-PERP'],
  90: ['WTI', 'WTI-PERP'],
  92: ['EURUSD', 'EURUSD-PERP'],
  94: ['GBPUSD', 'GBPUSD-PERP'],
  96: ['USDJPY', 'USDJPY-PERP'],
  98: ['QQQ', 'QQQ-PERP'],
  100: ['SPY', 'SPY-PERP'],
  102: ['AAPL', 'AAPL-PERP'],
  104: ['AMZN', 'AMZN-PERP'],
  106: ['GOOGL', 'GOOGL-PERP'],
  108: ['META', 'META-PERP'],
  110: ['MSFT', 'MSFT-PERP'],
  112: ['NVDA', 'NVDA-PERP'],
  114: ['TSLA', 'TSLA-PERP'],
  115: ['wQQQx', 'wQQQx-USDT0'],
  117: ['wSPYx', 'wSPYx-USDT0'],
} as const satisfies Record<number, readonly [string, string]>;

export const ARCA_INSTITUTIONAL_MARKET_DISPLAY_NAME_BY_PRODUCT_ID = {
  28: 'XAUT:USD',
  88: 'XAG:USD',
  90: 'WTI:USD',
  92: 'EURUSD:USD',
  94: 'GBPUSD:USD',
  96: 'USDJPY:USD',
  98: 'QQQ:USD',
  100: 'SPY:USD',
  102: 'AAPL:USD',
  104: 'AMZN:USD',
  106: 'GOOGL:USD',
  108: 'META:USD',
  110: 'MSFT:USD',
  112: 'NVDA:USD',
  114: 'TSLA:USD',
  115: 'QQQx/USDT0',
  117: 'SPYx/USDT0',
} as const satisfies Record<
  keyof typeof ARCA_INSTITUTIONAL_MARKET_BY_PRODUCT_ID,
  string
>;

const ARCA_INSTITUTIONAL_PRODUCT_IDS = new Set(
  Object.keys(ARCA_INSTITUTIONAL_MARKET_BY_PRODUCT_ID).map(Number),
);

export function isArcaInstitutionalMarketCategory(
  category: MarketCategory | undefined,
) {
  if (category == null) {
    return true;
  }

  return ARCA_INSTITUTIONAL_MARKET_CATEGORIES.includes(
    category as (typeof ARCA_INSTITUTIONAL_MARKET_CATEGORIES)[number],
  );
}

export function isArcaInstitutionalListedMarket({
  chainEnv,
  productId,
  metadata,
}: {
  chainEnv: string | undefined;
  productId: number;
  metadata: InstitutionalMetadata | undefined;
}) {
  if (chainEnv !== 'inkMainnet' && chainEnv !== 'inkTestnet') {
    return false;
  }

  if (!ARCA_INSTITUTIONAL_PRODUCT_IDS.has(productId) || !metadata) {
    return false;
  }

  const expected = ARCA_INSTITUTIONAL_MARKET_BY_PRODUCT_ID[
    productId as keyof typeof ARCA_INSTITUTIONAL_MARKET_BY_PRODUCT_ID
  ];

  if (!expected) {
    return false;
  }

  const [symbol, marketName] = expected;
  const hasAllowedCategory = ARCA_INSTITUTIONAL_MARKET_CATEGORIES.some(
    (category) => metadata.marketCategories?.has(category),
  );

  return (
    getInstitutionalMetadataSymbol(metadata) === symbol &&
    metadata.marketName === marketName &&
    hasAllowedCategory
  );
}

export function getArcaInstitutionalMarketDisplayName({
  chainEnv,
  productId,
  metadata,
}: {
  chainEnv: string | undefined;
  productId: number;
  metadata: InstitutionalMetadata | undefined;
}) {
  if (
    !isArcaInstitutionalListedMarket({
      chainEnv,
      productId,
      metadata,
    })
  ) {
    return metadata?.marketName ?? '';
  }

  return ARCA_INSTITUTIONAL_MARKET_DISPLAY_NAME_BY_PRODUCT_ID[
    productId as keyof typeof ARCA_INSTITUTIONAL_MARKET_DISPLAY_NAME_BY_PRODUCT_ID
  ];
}

export function getArcaInstitutionalMarketSearchString({
  chainEnv,
  productId,
  metadata,
}: {
  chainEnv: string | undefined;
  productId: number;
  metadata: InstitutionalMetadata | undefined;
}) {
  const rawMarketName = metadata?.marketName ?? '';
  const rawSymbol = getInstitutionalMetadataSymbol(metadata);
  const displayName = getArcaInstitutionalMarketDisplayName({
    chainEnv,
    productId,
    metadata,
  });

  return `${rawMarketName} ${rawSymbol ?? ''} ${displayName}`.trim();
}

function getInstitutionalMetadataSymbol(
  metadata: InstitutionalMetadata | undefined,
) {
  return metadata?.symbol ?? metadata?.token?.symbol;
}
